import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppWindow, ArrowRightLeft, ChevronDown, Code2, Database, Globe, House, Link2, LogOut, Mail, Monitor, Moon, Network, Search, ShieldCheck, Sun, UserRound } from "lucide-react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { cn, DropdownPrimitive as M, useTheme } from "@hostdock/ui";
import { api } from "./api";
import { CommandPalette, useCommandPaletteShortcut } from "./lib/command-palette";
import { NotificationBell } from "./lib/notification-bell";
import { useSession } from "./session";
import { PanelShell, type ShellNavItem } from "./shell";

/** Only implemented sections are listed. Groups are separated by a rule in the sidebar. */
const NAV: ShellNavItem[][] = [
  [
    { label: "Home", to: "/", icon: House },
    { label: "Websites", to: "/websites", icon: Globe },
    { label: "App Installer", to: "/apps", icon: AppWindow },
    { label: "Domains", to: "/domains", icon: Link2 },
    { label: "Zone Editor", to: "/dns", icon: Network },
    { label: "Databases", to: "/databases", icon: Database },
    { label: "Email", to: "/email", icon: Mail },
  ],
  [
    { label: "Migrate", to: "/migrate", icon: ArrowRightLeft },
    { label: "Developers", to: "/developers", icon: Code2 },
  ],
  [
    { label: "Security", to: "/security", icon: ShieldCheck },
    { label: "Account", to: "/account", icon: UserRound },
  ],
];

/** Pages ask the layout to open the command palette (e.g. "View all tools") with this event. */
export const OPEN_PALETTE_EVENT = "hostdock:open-palette";
export const openCommandPalette = () => window.dispatchEvent(new Event(OPEN_PALETTE_EVENT));

/** Documentation: a configured docs site, otherwise the in-app developer guide (never a dead link). */
export const DOCS_HREF = import.meta.env.VITE_DOCS_URL || "/developers";

export function PanelLayout() {
  const location = useLocation();
  const [palette, setPalette] = useState(false);
  useCommandPaletteShortcut(() => setPalette((o) => !o));
  useEffect(() => {
    const open = () => setPalette(true);
    window.addEventListener(OPEN_PALETTE_EVENT, open);
    return () => window.removeEventListener(OPEN_PALETTE_EVENT, open);
  }, []);
  return (
    <PanelShell
      nav={NAV}
      currentPath={location.pathname}
      docsHref={DOCS_HREF}
      topbar={
        <div className="flex w-full items-center gap-2 sm:gap-[11px]">
          <button
            type="button"
            onClick={() => setPalette(true)}
            aria-label="Search pages and actions"
            className="flex h-9 min-w-0 flex-[0_1_488px] items-center gap-2.5 rounded-lg border border-[#e2edf2] bg-[#f4f9fb] px-3 text-[12px] text-[#6c799d] transition-colors hover:border-line-strong hover:bg-surface dark:border-line dark:bg-surface-2 dark:text-subtle"
          >
            <Search size={16} className="shrink-0 text-muted" />
            <span className="min-w-0 flex-1 truncate text-left whitespace-nowrap">Search websites, domains, databases…</span>
            <kbd className="hidden shrink-0 rounded-[5px] border border-line bg-surface px-1.5 py-[1px] font-sans text-[10.5px] font-medium text-fg/80 sm:inline">Ctrl K</kbd>
          </button>
          <div className="flex-1" />
          <SystemStatus />
          <div className="md:ml-[1px]"><NotificationBell /></div>
          <div className="lg:ml-[6px]"><UserMenu /></div>
        </div>
      }
    >
      <Outlet />
      <CommandPalette open={palette} onOpenChange={setPalette} />
    </PanelShell>
  );
}

/** Derived from the customer's own websites: every one online, or how many need attention. */
function SystemStatus() {
  const q = useQuery({ queryKey: ["hosting-usage"], queryFn: api.usage, refetchInterval: 60_000 });
  if (!q.data) return null;
  const attention = q.data.websites.filter((w) => w.status !== "ACTIVE").length;
  const ok = attention === 0;
  return (
    <span
      className="hidden h-[34px] shrink-0 items-center gap-[10px] rounded-lg border border-[#e7eff3] bg-surface px-[13px] text-[11px] text-[#2f8466] md:inline-flex dark:border-line dark:text-success"
      title={ok ? "Every website on your account is online" : `${attention} website(s) are not online`}
    >
      <span className={cn("h-2 w-2 rounded-full", ok ? "bg-[#04ca5c]" : "bg-warning")} aria-hidden />
      {ok ? "All systems operational" : `${attention} website${attention === 1 ? "" : "s"} need${attention === 1 ? "s" : ""} attention`}
    </span>
  );
}

const THEME_ICON = { system: Monitor, light: Sun, dark: Moon } as const;
const THEME_NEXT = { system: "light", light: "dark", dark: "system" } as const;

function UserMenu() {
  const { session, logout } = useSession();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const ThemeIcon = THEME_ICON[preference];
  const item = "flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] text-fg outline-none select-none data-[highlighted]:bg-surface-2";
  return (
    <M.Root>
      <M.Trigger asChild>
        <button type="button" className="flex shrink-0 items-center gap-[9px] rounded-xl py-1 pr-1 pl-0 text-left transition-colors hover:bg-surface-2" aria-label="Account menu">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(140deg,#17b3c2,#0b8fa1)] text-[15px] font-medium text-white shadow-[0_2px_6px_rgb(11_143_161/0.3)]">{session.user.name.trim().charAt(0).toUpperCase() || "?"}</span>
          <span className="hidden min-w-0 leading-tight lg:block">
            <span className="block max-w-[150px] truncate text-[12px] font-semibold text-[#1b2559] dark:text-fg">{session.user.name}</span>
            <span className="mt-[2px] block max-w-[150px] truncate text-[11px] text-[#7988a7]">{session.customer.displayName}</span>
          </span>
          <ChevronDown size={16} className="ml-[11px] hidden text-[#1b2559] lg:block dark:text-fg" />
        </button>
      </M.Trigger>
      <M.Portal>
        <M.Content align="end" sideOffset={6} className="z-50 min-w-56 rounded-xl border border-line bg-surface p-1.5 shadow-lg">
          <div className="px-2.5 py-2">
            <p className="truncate text-[13px] font-semibold">{session.user.name}</p>
            <p className="truncate text-[12px] text-subtle">{session.user.email}</p>
          </div>
          <M.Separator className="my-1 h-px bg-line" />
          <M.Item className={item} onSelect={() => navigate("/account")}><UserRound size={15} />Account</M.Item>
          <M.Item className={item} onSelect={() => navigate("/security")}><ShieldCheck size={15} />Security</M.Item>
          <M.Item className={item} onSelect={(e) => { e.preventDefault(); setPreference(THEME_NEXT[preference]); }}>
            <ThemeIcon size={15} />Theme: {preference.charAt(0).toUpperCase() + preference.slice(1)}
          </M.Item>
          <M.Separator className="my-1 h-px bg-line" />
          <M.Item className={cn(item, "text-danger")} onSelect={() => void logout()}><LogOut size={15} />Sign out</M.Item>
        </M.Content>
      </M.Portal>
    </M.Root>
  );
}
